import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Rocket, ExternalLink, Loader2, CheckCircle2, AlertCircle, Github, X, Radar } from "lucide-react";
import { api } from "../lib/api";
import CustomDomainSteps from "./CustomDomainSteps";

export default function DeploymentsPanel({ blueprintId, refreshKey }) {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!blueprintId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/website/deployments?blueprint_id=${encodeURIComponent(blueprintId)}`);
      setDeployments(data || []);
    } catch {
      // silent — user may not be logged in yet
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [blueprintId, refreshKey]);

  if (!blueprintId) return null;
  if (!loading && deployments.length === 0) return null;

  return (
    <div className="card-soft p-4" data-testid="deployments-panel">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="w-4 h-4 text-marigold" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink flex-1">Deployments</h3>
        <span className="text-[10px] text-ink2">{deployments.length}</span>
      </div>
      {loading && (
        <div className="text-center py-4 text-xs text-ink2 flex items-center justify-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Checking status…
        </div>
      )}
      <ul className="space-y-2">
        {deployments.map((d) => (
          <DeploymentItem key={d.id} deployment={d} onChange={load} />
        ))}
      </ul>
    </div>
  );
}

function DeploymentItem({ deployment, onChange }) {
  const [liveUrl, setLiveUrl] = useState(deployment.expected_url_hint || "");
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [autoPolling, setAutoPolling] = useState(false);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);
  const MAX_ATTEMPTS = 20; // 20 * 30s = 10 min

  // Auto-poll expected_url_hint for pending deployments so users don't have to paste manually.
  useEffect(() => {
    // Only auto-poll fresh pending deploys that carry an expected URL.
    if (deployment.status !== "pending" || !deployment.expected_url_hint) return;
    // Skip if the deployment was created more than 15 min ago (user gave up / already live elsewhere).
    const created = new Date(deployment.created_at || 0).getTime();
    if (!isFinite(created) || Date.now() - created > 15 * 60 * 1000) return;

    setAutoPolling(true);
    attemptsRef.current = 0;

    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.post(`/website/deployments/${deployment.id}/verify`, {
          live_url: deployment.expected_url_hint,
        });
        if (data.status === "live") {
          setAutoPolling(false);
          if (timerRef.current) clearInterval(timerRef.current);
          toast.success("Your site is live! 🎉", { duration: 6000 });
          onChange && onChange();
          return;
        }
      } catch {
        // ignore — poll again on schedule
      }
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setAutoPolling(false);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    };

    // First check after 45s (Vercel usually takes ~30-60s for first build), then every 30s.
    const initial = setTimeout(() => {
      tick();
      timerRef.current = setInterval(tick, 30000);
    }, 45000);

    return () => {
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
      setAutoPolling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployment.id, deployment.status]);

  const verify = async () => {
    if (!liveUrl.trim()) return toast.error("Paste the URL Vercel gave you.");
    setVerifying(true);
    try {
      const { data } = await api.post(`/website/deployments/${deployment.id}/verify`, { live_url: liveUrl.trim() });
      if (data.status === "live") toast.success("Your site is live! 🎉");
      else toast.warning("URL didn't respond — try again in a minute?");
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify.");
    } finally { setVerifying(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this deployment record?")) return;
    setRemoving(true);
    try {
      await api.delete(`/website/deployments/${deployment.id}`);
      toast.success("Removed");
      onChange && onChange();
    } catch { toast.error("Could not remove."); }
    finally { setRemoving(false); }
  };

  const statusPill =
    deployment.status === "live" ? { icon: <CheckCircle2 className="w-3 h-3" />, label: "Live", cls: "bg-teal/15 text-teal border-teal/40" }
    : deployment.status === "unreachable" ? { icon: <AlertCircle className="w-3 h-3" />, label: "Not reachable", cls: "bg-marigold/15 text-marigold border-marigold/40" }
    : autoPolling ? { icon: <Radar className="w-3 h-3 animate-pulse" />, label: "Waiting for Vercel", cls: "bg-saffron/15 text-marigold border-saffron/40" }
    : { icon: <Loader2 className="w-3 h-3 animate-pulse" />, label: "Pending", cls: "bg-ink/10 text-ink border-ink/30" };

  return (
    <li className="rounded-xl border border-border p-3 bg-cream" data-testid={`deployment-${deployment.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusPill.cls}`}>
          {statusPill.icon} {statusPill.label}
        </span>
        <a
          href={deployment.repo_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-ink2 hover:text-ink inline-flex items-center gap-1 truncate"
          title={deployment.repo_url}
          data-testid={`deployment-repo-${deployment.id}`}
        >
          <Github className="w-3 h-3" />
          <span className="truncate max-w-[120px]">{deployment.repo_url.split("/").pop()}</span>
        </a>
        <button
          onClick={remove}
          disabled={removing}
          className="ml-auto text-ink2 hover:text-marigold p-1"
          title="Remove"
          aria-label="Remove deployment"
          data-testid={`deployment-remove-${deployment.id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {deployment.status === "live" && deployment.live_url ? (
        <a
          href={deployment.live_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs font-medium text-ink hover:text-marigold truncate inline-flex items-center gap-1"
          data-testid={`deployment-live-${deployment.id}`}
        >
          {deployment.live_url} <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <>
          <div className="text-[11px] text-ink2 mb-1.5" data-testid={`deployment-help-${deployment.id}`}>
            {autoPolling
              ? "Checking your Vercel URL every 30 seconds — hang tight."
              : "Paste your Vercel URL after deploy completes:"}
          </div>
          <div className="flex gap-1">
            <input
              type="url"
              value={liveUrl}
              onChange={(e) => setLiveUrl(e.target.value)}
              placeholder="https://your-site.vercel.app"
              className="flex-1 bg-paper border border-border rounded-lg px-2 py-1.5 text-xs outline-none focus:border-marigold"
              data-testid={`deployment-url-input-${deployment.id}`}
            />
            <button
              onClick={verify}
              disabled={verifying}
              className="text-xs px-3 py-1.5 rounded-lg bg-ink text-cream disabled:opacity-40 hover:bg-marigold transition"
              data-testid={`deployment-verify-${deployment.id}`}
            >
              {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify"}
            </button>
          </div>
        </>
      )}

      <div className="mt-3">
        <CustomDomainSteps deployment={deployment} onUpdated={onChange} />
      </div>
    </li>
  );
}
